import { useMemo, MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useNavigateControlbar } from '@nx-frontend/shared/hooks';
import { $lastPublishTime } from '@nx-frontend/shared/store/change-events-store';
import { setCustomizerPreviewModeEvent } from '@nx-frontend/shared/store/customizer-preview-store';
import { $dns } from '@nx-frontend/shared/store/dns';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $isCustomizerMode } from '@nx-frontend/shared/store/portal-app';
import { $sidebar, setIsPortalSettingsCollapsedAndSaveFx } from '@nx-frontend/shared/store/sidebar';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import {
  ControlbarEditEntityType,
  ControlbarEditParams,
  PortalServicePortalExtendedDtoCnameStatusEnum,
  UnleashFeatures,
} from '@nx-frontend/shared/types';

export enum CustomizerAdditionalLinkTypes {
  ANALYTICS = 'analytics',
  PORTAL_STYLE = 'portal-style',
  GENERAL_SETTINGS = 'general-settings',
  CLIENT_LINK = 'client-link',
  ACCESS_PERMISSIONS = 'access-permissions',
  CUSTOM_CODE = 'custom-code',
  AI_ASSISTANT_SETTINGS = 'ai-assistant-settings',
  CLIENT_SETTINGS = 'client-settings',
  DNS = 'dns',
}

export function getCustomizerAdditionalLink(type: CustomizerAdditionalLinkTypes, portalId: string) {
  const customizerUrl = `/portal/${portalId}`;
  switch (type) {
    case CustomizerAdditionalLinkTypes.ANALYTICS:
      return `${customizerUrl}/analytics`;
    case CustomizerAdditionalLinkTypes.GENERAL_SETTINGS:
      return `${customizerUrl}/settings`;
    case CustomizerAdditionalLinkTypes.CLIENT_LINK:
      return `${customizerUrl}/settings/magic-link-branding`;
    case CustomizerAdditionalLinkTypes.ACCESS_PERMISSIONS:
      return `${customizerUrl}/settings/access`;
    case CustomizerAdditionalLinkTypes.CUSTOM_CODE:
      return `${customizerUrl}/settings/customcode`;
    case CustomizerAdditionalLinkTypes.AI_ASSISTANT_SETTINGS:
      return `${customizerUrl}/settings/ai-assistant-settings`;
    case CustomizerAdditionalLinkTypes.DNS:
      return `${customizerUrl}/settings/dns`;
    case CustomizerAdditionalLinkTypes.CLIENT_SETTINGS:
      return `${customizerUrl}/settings/client-settings`;
    case CustomizerAdditionalLinkTypes.PORTAL_STYLE:
      return `?controlbar-entity-type=${ControlbarEditEntityType.THEME}`;
  }
}

export function getCustomizerAdditionalLinkId(type: CustomizerAdditionalLinkTypes, portalId: string) {
  switch (type) {
    case CustomizerAdditionalLinkTypes.ANALYTICS:
      return `${portalId}-analytics`;
    case CustomizerAdditionalLinkTypes.GENERAL_SETTINGS:
      return `${portalId}-settings`;
    case CustomizerAdditionalLinkTypes.CLIENT_LINK:
      return `${portalId}-settings-magic-link-branding`;
    case CustomizerAdditionalLinkTypes.ACCESS_PERMISSIONS:
      return `${portalId}-settings-access`;
    case CustomizerAdditionalLinkTypes.CUSTOM_CODE:
      return `${portalId}-settings-customcode`;
    case CustomizerAdditionalLinkTypes.AI_ASSISTANT_SETTINGS:
      return `${portalId}-settings-ai-assistant-settings`;
    case CustomizerAdditionalLinkTypes.DNS:
      return `${portalId}-settings-dns`;
    case CustomizerAdditionalLinkTypes.CLIENT_SETTINGS:
      return `${portalId}-settings-client-settings';`;
    case CustomizerAdditionalLinkTypes.PORTAL_STYLE:
      return `${portalId}-portal-style`;
  }
}

export function SidebarPortalLinks() {
  const [portalSettings, dns, unleashFlags, isCustomizerMode, sidebar, lastPublishTime] = useUnit([
    $portalSettings,
    $dns,
    $unleashFlags,
    $isCustomizerMode,
    $sidebar,
    $lastPublishTime,
  ]);

  const [setIsPortalSettingsCollapsedAndSave, setCustomizerPreviewMode] = useEvent([
    setIsPortalSettingsCollapsedAndSaveFx,
    setCustomizerPreviewModeEvent,
  ]);
  const currentPath = usePathname();
  const searchParams = useSearchParams();
  const navigateControlbar = useNavigateControlbar();

  const customizerUrl = useMemo(() => {
    return `/portal/${portalSettings.id}`;
  }, [portalSettings.id]);

  const t = useTranslations('sidebar');

  const portalStyleUrl = isCustomizerMode
    ? `${currentPath}${getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.PORTAL_STYLE, portalSettings.id)}`
    : `${customizerUrl}/visual${getCustomizerAdditionalLink(
        CustomizerAdditionalLinkTypes.PORTAL_STYLE,
        portalSettings.id
      )}`;

  const settingLinks = useMemo(() => {
    const items = [
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.PORTAL_STYLE, portalSettings.id),
        url: portalStyleUrl,
        title: t('sidebar-portal_style'),
        icon: IconsMap.EDIT,
        active: searchParams.get(ControlbarEditParams.ENTITY_TYPE) === ControlbarEditEntityType.THEME,
        action: (e: MouseEvent) => {
          if (isCustomizerMode) {
            e.preventDefault();
            setCustomizerPreviewMode({ type: 'default' });
            navigateControlbar({ entityType: ControlbarEditEntityType.THEME });
          }
        },
      },
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.GENERAL_SETTINGS, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.GENERAL_SETTINGS, portalSettings.id),
        title: t('sidebar-settings_general'),
        icon: IconsMap.PEN,
      },
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.CLIENT_LINK, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.CLIENT_LINK, portalSettings.id),
        title: t('sidebar-settings_client_link'),
        icon: IconsMap.LINK,
      },
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.ACCESS_PERMISSIONS, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.ACCESS_PERMISSIONS, portalSettings.id),
        title: t('sidebar-settings_permissions'),
        icon: IconsMap.INCOGNITO,
      },
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.CLIENT_SETTINGS, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.CLIENT_SETTINGS, portalSettings.id),
        title: t('sidebar-settings-client_settings'),
        icon: IconsMap.PERSONS,
      },
      {
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.CUSTOM_CODE, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.CUSTOM_CODE, portalSettings.id),
        title: t('sidebar-settings_custom_code'),
        icon: IconsMap.CODE_INLINE,
      },
      ...(unleashFlags[UnleashFeatures.AI_ASSISTANT_IN_PORTALS]
        ? [
            {
              id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.AI_ASSISTANT_SETTINGS, portalSettings.id),
              icon: IconsMap.MAGIC_1,
              title: t('sidebar-settings_ai'),
              url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.AI_ASSISTANT_SETTINGS, portalSettings.id),
            },
          ]
        : []),
    ];

    if (dns.cnameStatus !== PortalServicePortalExtendedDtoCnameStatusEnum.NA) {
      items.push({
        id: getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.DNS, portalSettings.id),
        url: getCustomizerAdditionalLink(CustomizerAdditionalLinkTypes.DNS, portalSettings.id),
        title: t('sidebar-settings_dns'),
        icon: IconsMap.DNS,
      });
    }

    return items;
  }, [
    portalSettings.id,
    portalStyleUrl,
    t,
    searchParams,
    unleashFlags,
    dns.cnameStatus,
    isCustomizerMode,
    setCustomizerPreviewMode,
    navigateControlbar,
  ]);

  const linkClass = 'flex gap-2 cursor-pointer items-center text-sm py-2 rounded';
  const iconClass = 'w-4 h-4 text-neutral-500';
  const activeClass = 'bg-teal-100 font-bold';

  return (
    <div className="pt-2">
      <div
        className={classNames(linkClass)}
        onClick={() => setIsPortalSettingsCollapsedAndSave(!sidebar.isPortalSettingsCollapsed)}
      >
        <span className="flex">
          <Icon
            className={classNames(
              'w-4 h-4',
              {
                '-rotate-90': sidebar.isPortalSettingsCollapsed,
              },
              iconClass
            )}
            icon={IconsMap.ARROW_DOWN}
          />
          <Icon className={iconClass} icon={IconsMap.COG} />
        </span>
        <span>{t('sidebar-settings')}</span>
      </div>
      {!sidebar.isPortalSettingsCollapsed && (
        <>
          {settingLinks.map((link) => {
            const isActive = link.url === currentPath || link.active;
            const params =
              lastPublishTime &&
              link.id !== getCustomizerAdditionalLinkId(CustomizerAdditionalLinkTypes.PORTAL_STYLE, portalSettings.id)
                ? `?ts=${lastPublishTime}`
                : '';
            return (
              <Link
                data-id={link.id}
                key={link.url}
                href={`${link.url}${params}`}
                className={classNames('pl-8', linkClass, {
                  [activeClass]: isActive,
                })}
                onClick={(event) => {
                  if (link.action) {
                    link.action(event);
                  }
                }}
              >
                <span>
                  <Icon className={iconClass} icon={link.icon} />
                </span>
                <span>{link.title}</span>
              </Link>
            );
          })}
        </>
      )}
    </div>
  );
}
